/**
 * CookieBanner.tsx
 *
 * Site-wide cookie consent notice. Renders a fixed bottom banner until the
 * user acknowledges it; the acknowledgement is persisted in localStorage so it
 * is shown once per browser. Required for HIPAA / PIPEDA disclosure of cookie
 * usage. Rendered globally from App.tsx so it appears on both public (login,
 * portal) and authenticated pages.
 *
 * Bilingual (EN / FR-CA) via the shared useUiLang preference; the EN/FR toggle
 * here also drives the login-page consent line.
 */
import { useEffect, useState } from 'react';
import { useUiLang, setUiLang, type UiLang } from '@/lib/uiLanguage';
import { useAnyModalOpen } from '@/components/ui/Modal';
import { useIsBelow } from '@/hooks/useBreakpoint';

const STORAGE_KEY = 'ftp.cookieConsent';
const TERMS_URL = 'https://prospera.ai/terms';
const PRIVACY_URL = '/privacy';

const COPY: Record<UiLang, {
  aria: string;
  before: string;
  terms: string;
  between: string;
  privacy: string;
  after: string;
  accept: string;
}> = {
  en: {
    aria: 'Cookie notice',
    before:
      'We use cookies that are essential to operate this platform and to keep your session secure. By continuing to use Follow That Patient you agree to our ',
    terms: 'Terms & Conditions',
    between: ' and ',
    privacy: 'Privacy Policy',
    after: '.',
    accept: 'Accept',
  },
  fr: {
    aria: 'Avis sur les témoins',
    before:
      "Nous utilisons des témoins essentiels au fonctionnement de cette plateforme et à la sécurité de votre session. En continuant d'utiliser Follow That Patient, vous acceptez nos ",
    terms: "Conditions d'utilisation",
    between: ' et notre ',
    privacy: 'Politique de confidentialité',
    after: '.',
    accept: 'Accepter',
  },
};

export function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const lang = useUiLang();
  const t = COPY[lang];
  const modalOpen = useAnyModalOpen();
  // `md`, matching the tier on which the modal becomes a bottom sheet — the geometry
  // that turns this overlap into a blocked form rather than a cosmetic one.
  const isSheetTier = useIsBelow('md');

  useEffect(() => {
    try {
      if (localStorage.getItem(STORAGE_KEY) !== 'accepted') {
        setVisible(true);
      }
    } catch {
      // localStorage unavailable (private mode / blocked) — show the notice.
      setVisible(true);
    }
  }, []);

  function accept() {
    try {
      localStorage.setItem(STORAGE_KEY, 'accepted');
    } catch {
      // ignore write failures; banner will simply reappear next load.
    }
    setVisible(false);
  }

  // Step aside while a modal is open, on the phone tier only.
  //
  // This bar is fixed to the bottom at z-[9999] and modals sit at z-50, so it paints
  // over the modal's action row. What makes that fatal rather than untidy is the phone:
  // below `md` the modal is a full-width sheet flush to the bottom edge, and this
  // banner's copy wraps to five lines there, burying the last field of the form along
  // with the buttons.
  //
  // Gated rather than applied at every width because the requirement on this effort is
  // that output at 1024px and above is unchanged (R2), and a banner that vanishes on
  // desktop whenever a dialog opens is a visible change to it. The same overlap exists
  // in principle on a wide screen, where the modal is centred and inset and the bar is a
  // single line — a much milder problem, pre-dating this work, and not what SCRUM-1353
  // reported. It needs its own ticket rather than a silent ride-along here.
  //
  // Nothing is accepted on the user's behalf: it returns on close, still awaiting a tap.
  if (!visible || (modalOpen && isSheetTier)) return null;

  return (
    <div
      role="dialog"
      aria-label={t.aria}
      aria-live="polite"
      className="fixed inset-x-0 bottom-0 z-[9999] border-t border-slate-200 bg-white/95 px-4 py-4 shadow-[0_-8px_30px_rgba(0,0,0,0.12)] backdrop-blur"
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm leading-relaxed text-slate-600">
          {t.before}
          <a
            href={TERMS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-blue-600 hover:underline"
          >
            {t.terms}
          </a>
          {t.between}
          <a
            href={PRIVACY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-blue-600 hover:underline"
          >
            {t.privacy}
          </a>
          {t.after}
        </p>
        <div className="flex shrink-0 items-center gap-3">
          {/* Language toggle (EN / FR-CA) — also drives the login consent line. */}
          <div className="flex items-center rounded-md border border-slate-200 text-xs font-medium">
            {(['en', 'fr'] as const).map((code) => (
              <button
                key={code}
                type="button"
                onClick={() => setUiLang(code)}
                aria-pressed={lang === code}
                className={
                  'px-2.5 py-1 uppercase transition-colors first:rounded-l-md last:rounded-r-md ' +
                  (lang === code
                    ? 'bg-slate-800 text-white'
                    : 'text-slate-500 hover:bg-slate-100')
                }
              >
                {code}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={accept}
            className="rounded-md bg-[#6558f5] px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#766af8]"
          >
            {t.accept}
          </button>
        </div>
      </div>
    </div>
  );
}
